/* ====================================================================
   VERDICT GOODS — vg-site.js (ported from source site.js)
   Reveals, parallax, rails/carousels, evidence-board strings, seal spin,
   full-screen menu. Stands down from reveal choreography when the GSAP
   layer (vg-motion.js) owns it. Theme additions: per-element guards and
   Shopify section-lifecycle re-init.
   ==================================================================== */
window.VerdictSite = (function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var VerdictSiteAPI = {};

  /* ---- clip-rise reveals ---- */
  function prepRise(el) {
    if (el.dataset.risePrepped) return;
    el.dataset.risePrepped = '1';
    Array.prototype.slice.call(el.children).forEach(function (child) {
      var shell = document.createElement('span');
      shell.className = 'rl';
      el.insertBefore(shell, child);
      shell.appendChild(child);
    });
    if (!el.children.length && el.textContent.trim()) {
      var txt = el.textContent; el.textContent = '';
      var shell = document.createElement('span'); shell.className = 'rl';
      var inner = document.createElement('span'); inner.textContent = txt;
      shell.appendChild(inner); el.appendChild(shell);
    }
  }
  function initRise(scope) {
    var els = (scope || document).querySelectorAll('[data-rise]');
    if (!els.length) return;
    els.forEach(prepRise);
    if (reduce) { els.forEach(function (el) { el.classList.add('in'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: .3, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (el) { if (!el.classList.contains('in')) io.observe(el); });
  }

  /* ---- fly-in prints ---- */
  function initFly(scope) {
    var els = (scope || document).querySelectorAll('.fly');
    if (!els.length) return;
    if (reduce) { els.forEach(function (el) { el.classList.add('in'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      });
    }, { threshold: .22 });
    els.forEach(function (el) { if (!el.classList.contains('in')) io.observe(el); });
  }

  /* ---- parallax drift: [data-drift="0.12"] ---- */
  function initDrift() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-drift]'));
    if (!els.length || reduce) return;
    if (document.body.dataset.vgDrift) return;
    document.body.dataset.vgDrift = '1';
    var ticking = false;
    function upd() {
      var vh = window.innerHeight;
      document.querySelectorAll('[data-drift]').forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        var center = r.top + r.height / 2 - vh / 2;
        var f = parseFloat(el.getAttribute('data-drift')) || .1;
        el.style.transform = 'translateY(' + (-center * f).toFixed(1) + 'px)' +
          (el.dataset.driftRot ? ' rotate(' + el.dataset.driftRot + 'deg)' : '');
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(upd); ticking = true; }
    }, { passive: true });
    upd();
  }

  /* ---- countdown digit pulse ---- */
  function initCountPulse(scope) {
    if (reduce || !window.MutationObserver) return;
    (scope || document).querySelectorAll('.count [data-u]').forEach(function (unit) {
      if (unit.dataset.vgPulse) return;
      unit.dataset.vgPulse = '1';
      var mo = new MutationObserver(function () {
        unit.classList.remove('pulse');
        void unit.offsetWidth;
        unit.classList.add('pulse');
      });
      mo.observe(unit, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ---- stagger groups ---- */
  function initStagger(scope) {
    var groups = (scope || document).querySelectorAll('[data-stagger]');
    if (!groups.length) return;
    groups.forEach(function (group) {
      Array.prototype.slice.call(group.children).forEach(function (child, i) {
        child.style.transitionDelay = (i * 0.09) + 's';
        child.classList.add('stag');
      });
    });
    if (reduce) {
      groups.forEach(function (g) { Array.prototype.slice.call(g.children).forEach(function (c) { c.classList.add('in'); }); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          Array.prototype.slice.call(e.target.children).forEach(function (c) { c.classList.add('in'); });
          io.unobserve(e.target);
        }
      });
    }, { threshold: .15, rootMargin: '0px 0px -6% 0px' });
    groups.forEach(function (g) { io.observe(g); });
  }

  /* ---- slow hero push-in ---- */
  function initZoomSlow(scope) {
    if (reduce) return;
    (scope || document).querySelectorAll('.zoom-slow img').forEach(function (img) {
      if (img.dataset.vgZoomed) return;
      img.dataset.vgZoomed = '1';
      img.animate([{ transform: 'scale(1.08)' }, { transform: 'scale(1)' }],
        { duration: 8000, easing: 'cubic-bezier(.2,.6,.3,1)', fill: 'forwards' });
    });
  }

  /* ---- hero headline scroll drift ---- */
  function initHeroDrift() {
    var h1 = document.querySelector('[data-hero-headline]');
    if (!h1 || reduce || h1.dataset.vgDrifting) return;
    h1.dataset.vgDrifting = '1';
    var ticking = false;
    function upd() {
      var vh = window.innerHeight;
      var p = Math.min(1, Math.max(0, window.scrollY / vh));
      h1.style.transform = 'translateY(' + (-p * vh * 0.15) + 'px) scale(' + (1 - p * 0.06) + ')';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(upd); ticking = true; }
    }, { passive: true });
    upd();
  }

  /* ---- seal velocity spin ---- */
  function initSealVelocity(scope) {
    if (reduce) return;
    (scope || document).querySelectorAll('.seal .ring').forEach(function (ring) {
      if (ring.dataset.vgSpin) return;
      ring.dataset.vgSpin = '1';
      ring.style.animation = 'none';
      var IDLE = 18;
      var angle = 0, rate = IDLE, target = IDLE;
      var lastY = window.scrollY, lastT = performance.now();
      var raf = null, visible = false, prev = 0;
      window.addEventListener('scroll', function () {
        var now = performance.now(), dt = Math.max(16, now - lastT);
        var v = Math.abs(window.scrollY - lastY) / dt;
        target = IDLE + Math.min(320, v * 260);
        lastY = window.scrollY; lastT = now;
      }, { passive: true });
      function tick(now) {
        var dt = Math.min(64, now - (prev || now));
        prev = now;
        target += (IDLE - target) * Math.min(1, dt / 420);
        rate += (target - rate) * Math.min(1, dt / 160);
        angle = (angle + rate * dt / 1000) % 360;
        ring.style.transform = 'rotate(' + angle.toFixed(2) + 'deg)';
        raf = visible ? requestAnimationFrame(tick) : null;
      }
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible && !raf) { prev = 0; raf = requestAnimationFrame(tick); }
      }).observe(ring);
    });
  }

  /* ---- tab-blur title flip (message set in Theme settings; blank = off) ---- */
  function initTitleFlip() {
    if (document.body.dataset.vgTitleFlip) return;
    document.body.dataset.vgTitleFlip = '1';
    var away = (window.VG && window.VG.tabAwayTitle || '').trim();
    if (!away) return;
    var orig = document.title;
    window.addEventListener('blur', function () { document.title = away; });
    window.addEventListener('focus', function () { document.title = orig; });
  }

  /* ---- horizontal scrollers ([data-rail]) ---- */
  function initRails(scope) {
    (scope || document).querySelectorAll('[data-rail]').forEach(function (scroller) {
      if (scroller.dataset.vgRail) return;
      scroller.dataset.vgRail = '1';
      var track = scroller.querySelector('[data-rail-track]') || scroller.firstElementChild;
      if (!track) return;
      var ui = scroller.parentElement && scroller.parentElement.querySelector('[data-rail-ui]');
      var thumb = ui && ui.querySelector('[data-rail-thumb]');
      var prev = ui && ui.querySelector('[data-rail-prev]');
      var next = ui && ui.querySelector('[data-rail-next]');
      function step() {
        var kids = [].filter.call(track.children, function (c) {
          return getComputedStyle(c).position !== 'absolute';
        });
        var first = kids[0], second = kids[1];
        if (!first) return scroller.clientWidth;
        var w = first.getBoundingClientRect().width;
        var gap = second ? second.getBoundingClientRect().left - first.getBoundingClientRect().right : 0;
        return w + Math.max(0, gap);
      }
      function sync() {
        var max = scroller.scrollWidth - scroller.clientWidth;
        var scrollable = max > 2;
        if (ui) ui.hidden = !scrollable;
        if (!scrollable) return;
        if (thumb) {
          var frac = scroller.clientWidth / scroller.scrollWidth;
          thumb.style.width = (frac * 100) + '%';
          thumb.style.transform = 'translateX(' + (scroller.scrollLeft / max) * ((1 - frac) / frac) * 100 + '%)';
        }
        if (prev) prev.disabled = scroller.scrollLeft < 4;
        if (next) next.disabled = scroller.scrollLeft > max - 4;
      }
      if (prev) prev.addEventListener('click', function () { scroller.scrollLeft -= step(); });
      if (next) next.addEventListener('click', function () { scroller.scrollLeft += step(); });
      scroller.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      window.addEventListener('load', sync);
      sync();
      if (window.MutationObserver) new MutationObserver(sync).observe(track, { childList: true });
    });
  }

  /* ---- evidence-board strings: per [data-board] wrapper (was #bdWall/#bdStrings) ---- */
  function initBoardStrings(scope) {
    (scope || document).querySelectorAll('[data-board]').forEach(function (wall) {
      if (wall.dataset.vgBoard) return;
      wall.dataset.vgBoard = '1';
      var svg = wall.querySelector('.bd-strings');
      if (!svg) return;
      var mob = window.matchMedia('(max-width:860px)');
      function pt(el, br) { var r = el.getBoundingClientRect(); return { x: r.left - br.left + r.width / 2, y: r.top - br.top + r.height / 2 }; }
      function draw() {
        var br = wall.getBoundingClientRect();
        if (!br.width || !br.height) return false;
        svg.setAttribute('viewBox', '0 0 ' + br.width + ' ' + br.height);
        var d = '';
        if (mob.matches) {
          var cards = [].slice.call(wall.querySelectorAll('.bd-print'));
          for (var i = 0; i < cards.length - 1; i++) {
            var cr = cards[i].getBoundingClientRect();
            var a = { x: cr.left - br.left + cr.width / 2, y: cr.bottom - br.top - 4 };
            var pin = cards[i + 1].querySelector('.bd-pin');
            if (!pin) continue;
            var b = pt(pin, br);
            var mx = (a.x + b.x) / 2 + (i % 2 ? -46 : 46), my = (a.y + b.y) / 2 + 14;
            d += '<path d="M' + a.x + ',' + a.y + ' Q' + mx + ',' + my + ' ' + b.x + ',' + b.y + '" pathLength="1"/>';
          }
        } else {
          var hub = wall.querySelector('.bd-cookie .bd-pin');
          if (!hub) return true;
          var cookie = pt(hub, br);
          [].forEach.call(wall.querySelectorAll('.bd-print:not(.bd-cookie) .bd-pin'), function (p) {
            var a = pt(p, br);
            d += '<path d="M' + a.x + ',' + a.y + ' L' + cookie.x + ',' + cookie.y + '" pathLength="1"/>';
          });
        }
        svg.innerHTML = d;
        return true;
      }
      function drawWhenReady(tries) {
        if (draw()) return;
        if ((tries || 0) > 40) return;
        setTimeout(function () { drawWhenReady((tries || 0) + 1); }, 120);
      }
      var t;
      function redraw() { clearTimeout(t); t = setTimeout(draw, 60); }
      drawWhenReady(0);
      window.addEventListener('load', redraw);
      window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(draw, 180); });
      [].forEach.call(wall.querySelectorAll('.bd-print'), function (p) {
        p.addEventListener('transitionend', redraw);
      });
      if (window.ResizeObserver) new ResizeObserver(redraw).observe(wall);
      VerdictSiteAPI.redrawBoardStrings = redraw;
    });
  }

  /* ---- waitlist folder stage (ported from the source page's inline script) ---- */
  function initWlStage(scope) {
    (scope || document).querySelectorAll('[data-wl-stage]').forEach(function (stage) {
      if (stage.dataset.vgWl) return;
      stage.dataset.vgWl = '1';
      var tabs = Array.prototype.slice.call(stage.querySelectorAll('.wl-tab'));
      function setOpen(n) {
        var next = stage.getAttribute('data-open') === n ? '0' : n;
        stage.setAttribute('data-open', next);
        tabs.forEach(function (t, j) { t.setAttribute('aria-expanded', String(j + 1) === next ? 'true' : 'false'); });
      }
      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { setOpen(String(i + 1)); });
      });
      /* the folder color band and the sticky note toggle too — the tab alone
         is a small target and people click the folder itself */
      stage.addEventListener('click', function (e) {
        if (!e.target.closest) return;
        var el = e.target.closest('.wl-body, .wl-sticky');
        if (!el || !stage.contains(el)) return;
        var m = el.className.match(/--(\d)/);
        if (m) setOpen(m[1]);
      });
    });
  }

  /* ---- full-screen menu ---- */
  function initMenu() {
    var btn = document.getElementById('menuBtn'), menu = document.getElementById('menu'),
      close = document.getElementById('menuClose');
    if (!btn || !menu || btn.dataset.vgMenu) return;
    btn.dataset.vgMenu = '1';
    var lastFocus = null;
    function open() {
      lastFocus = document.activeElement;
      menu.hidden = false;
      requestAnimationFrame(function () { menu.classList.add('open'); });
      document.body.classList.add('menu-open');
      btn.setAttribute('aria-expanded', 'true');
      if (close) close.focus();
    }
    function shut() {
      menu.classList.remove('open');
      document.body.classList.remove('menu-open');
      btn.setAttribute('aria-expanded', 'false');
      setTimeout(function () { menu.hidden = true; }, reduce ? 0 : 500);
      if (lastFocus) lastFocus.focus();
    }
    btn.addEventListener('click', open);
    if (close) close.addEventListener('click', shut);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('open')) shut();
    });
    menu.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var f = menu.querySelectorAll('button,a[href]');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  function initScope(scope) {
    if (!window.VG_MOTION) {
      initRise(scope);
      initFly(scope);
      initStagger(scope);
      initZoomSlow(scope);
    }
    initCountPulse(scope);
    initSealVelocity(scope);
    initBoardStrings(scope);
    initRails(scope);
    initWlStage(scope);
  }

  function initAll() {
    if (!window.VG_MOTION) {
      initRise();
      initFly();
      initDrift();
      initStagger();
      initZoomSlow();
      initHeroDrift();
    }
    initCountPulse();
    initSealVelocity();
    initTitleFlip();
    initBoardStrings();
    initRails();
    initWlStage();
    initMenu();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', function (e) {
    initScope(e.target);
    initMenu();
  });

  VerdictSiteAPI.reduce = reduce;
  VerdictSiteAPI.initRise = initRise;
  VerdictSiteAPI.initFly = initFly;
  VerdictSiteAPI.initStagger = initStagger;
  VerdictSiteAPI.initScope = initScope;
  return VerdictSiteAPI;
})();
